<template>
  <div>
    <!-- Radio Channel Mini Widget-->
    <v-list-tile
      v-if="channel && !expanded"
      class="mt-2"
      @click="expanded = true"
    >
      <v-list-tile-avatar tile>
        <img
          v-if="channel.cover"
          :src="channel.cover"
          style="width: 40px; height: 40px"
        >
        <v-icon
          v-else
          medium
          style="width: 40px; height: 40px"
        >
          music_note
        </v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title class="grey--text text--darken-3">
          {{ channel.name }}
        </v-list-tile-title>
        <v-list-tile-sub-title>{{ channel.description }}</v-list-tile-sub-title>
      </v-list-tile-content>
      <v-list-tile-action>
        <v-icon
          large
          class="grey--text text--darken-1 mr-1"
          style="width: 24px"
        >
          keyboard_arrow_down
        </v-icon>
      </v-list-tile-action>
    </v-list-tile>
    <!-- Radio Channel Detail Widget -->
    <v-slide-y-transition>
      <div v-if="channel && expanded">
        <list-header
          :label="channel.name"
          show-expander
          :expanded.sync="expanded"
        />
        <v-layout
          v-bind="channelLayout"
          justify-center
          :align-center="channelLayout.column"
          class="mt-2"
        >
          <div :class="{'mr-4': channelLayout.row}">
            <img
              v-if="channel.cover"
              :src="channel.cover"
              width="240"
              height="240"
            >
            <v-icon
              v-else
              style="font-size: 8em; width: 240px; height: 240px"
            >
              music_note
            </v-icon>
          </div>
          <v-layout
            column
            :class="{'mt-4': channelLayout.column, 'mt-2': channelLayout.row}"
          >
            <v-layout>
              <v-select
                :label="channelDropdownLabel"
                :value="channel"
                :items="radioChannels"
                :loading="busy"
                :disabled="disableDropdown"
                class="v-select-label-full-width mb-3"
                item-text="name"
                item-value="id"
                return-object
                @input="onChannelSelected"
              >
                <template
                  slot="item"
                  slot-scope="data"
                >
                  <v-list-tile-avatar tile>
                    <img
                      v-if="data.item.cover"
                      :src="data.item.cover"
                      style="width: 40px; height: 40px"
                    >
                    <v-icon
                      v-else
                      medium
                      style="width: 40px; height: 40px"
                    >
                      {{ data.item.icon || 'music_note' }}
                    </v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ makeShorterChannelDescription(data.item.description) }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </template>
              </v-select>
            </v-layout>
            <!-- Sample .mp3 -->
            <v-layout
              v-if="channel.sample"
              column
            >
              <v-subheader class="pa-0 mb-2 auto-height">
                {{ $t('radioChannelSample') }}
              </v-subheader>
              <audio
                :src="channel.sample"
                preload="metadata"
                controls
                controlslist="nodownload"
                style="width: 100%"
              />
            </v-layout>
            <blockquote class="blockquote px-0">
              {{ channel.description }}
            </blockquote>
            <v-spacer />
            <v-layout
              v-bind="channelLayout"
              justify-end
            >
              <v-btn
                color="msr"
                flat
                round
                small
                @click="$router.push('/channel/' + channel.id)"
              >
                {{ $t('toRadioChannel') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-layout>
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script>
import ListHeader from '@/components/ListHeader';

export default {
  name: 'RadioChannelListTile',
  components: {
    ListHeader,
  },
  props: {
    channel: {
      type: Object,
      required: true,
    },
    oldChannel: {
      type: Object,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    disableDropdown: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
    };
  },
  computed: {
    channelLayout() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return {row: true};
      }
      return {column: true};
    },
    channelDropdownLabel() {
      let label = this.$t('radioChannelChange');
      if (!this.oldChannel) {
        return label;
      }
      return (
        label +
        ' (' +
        this.$t('radioChannelOld') +
        ': "' +
        this.oldChannel.name +
        '")'
      );
    },
    /**
     * List of Channel objects plus group and header entries.
     */
    radioChannels() {
      const channels = this.$store.state.common.radioChannels;

      const topChannels = channels.filter((chan) => !chan.playlist_channel);
      const bottomChannels = channels.filter((chan) => chan.playlist_channel);

      let items = [];
      if (topChannels.length > 0) {
        items.push({header: this.$t('radioChannels')});
        items = items.concat(topChannels);
      }
      if (bottomChannels.length > 0) {
        items.push({header: this.$t('playlistChannels')});
        items = items.concat(bottomChannels);
      }
      return items;
    },
  },
  watch: {
    expanded: function (val) {
      if (val) {
        this.$store.dispatch('LIST_RADIO_CHANNELS');
      }
    },
    'channel.id': function (newVal, oldVal) {
      if (oldVal === undefined) {
        // Initial assignment, we can ignore this.
        return;
      }
      // User has picked a new Channel.
      this.$emit('selected', this.channel);
    },
  },
  methods: {
    makeShorterChannelDescription(description) {
      const maxLength = 70;
      if (description.length <= maxLength) {
        return description;
      }
      return description.slice(0, maxLength - 3) + '...';
    },
    onChannelSelected(channel) {
      this.$emit('update:oldChannel', this.channel);
      this.$emit('update:channel', channel);
    },
  },
};
</script>

<style>
.v-select-label-full-width.v-input
  .v-input__control
  .v-input__slot
  .v-menu
  .v-menu__activator
  .v-select__slot
  label {
  max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/RadioChannelListTile.vue